import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { 
  Search, 
  Calendar, 
  ChevronRight, 
  RefreshCw, 
  X, 
  ShoppingBag, 
  ArrowLeftRight, 
  FileText, 
  AlertTriangle, 
  Printer, 
  CheckCircle2, 
  Undo2 
} from "lucide-react";
import { apiClient } from "../../apiclient/apiclient";
import { formatAnyDateToDDMMYYYY } from "../../utils/dateFormatter";
import { toast } from "react-toastify";

const SearchableClientSelect = ({
  options = [],
  value,
  onChange,
  placeholder,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  React.useEffect(() => {
    const selected = options.find((o) => o._id === value);
    if (selected) {
      setQuery(selected.name || "");
    } else {
      setQuery("");
    }
  }, [value, options]);

  const filtered = options.filter((o) =>
    `${o.name || ""} ${o.phone || ""}`
      .toLowerCase()
      .includes(query.toLowerCase())
  );

  return (
    <div className="relative w-full max-w-xl" ref={ref}>
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
        <Search size={16} />
      </div>
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg pl-10 pr-10 py-2 text-sm bg-white outline-none transition w-full text-slate-800"
      />
      {query && (
        <button
          onClick={() => {
            setQuery("");
            onChange("");
          }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X size={16} />
        </button>
      )}

      {open && (
        <ul className="absolute w-full bg-white border border-slate-200 rounded-lg mt-1 max-h-60 overflow-y-auto shadow-lg z-50 py-1">
          {filtered.length === 0 && (
            <li className="px-4 py-2.5 text-xs text-slate-400 italic">No clients found</li>
          )}
          {filtered.map((o) => (
            <li
              key={o._id}
              onClick={() => {
                onChange(o._id);
                setQuery(o.name || "");
                setOpen(false);
              }}
              className={`px-4 py-2.5 text-xs hover:bg-blue-50 hover:text-blue-900 cursor-pointer transition border-b border-slate-50 last:border-b-0 flex justify-between items-center ${
                value === o._id ? "bg-blue-50 text-blue-900 font-bold" : "text-slate-700"
              }`}
            >
              <span>{o.name}</span>
              {o.phone && <span className="text-[10px] text-slate-400 font-medium">{o.phone}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ReturnOrder = () => {
  /* ================= STATE ================= */
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  
  const [clientOrders, setClientOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [returnQuantities, setReturnQuantities] = useState({}); // { [itemId]: qty }
  const [dispositions, setDispositions] = useState({}); // { [itemId]: "restock" | "dispose" }
  
  const [notes, setNotes] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [showReceipt, setShowReceipt] = useState(false);
  const [generatedReceipt, setGeneratedReceipt] = useState(null);

  /* ================= FETCH CLIENTS ================= */
  useEffect(() => {
    const fetchClients = async () => {
      try {
        setLoadingClients(true);
        const res = await apiClient.get("/clients");
        const sorted = (res.data?.clients || []).sort((a, b) =>
          a.name.localeCompare(b.name)
        );
        setClients(sorted);
      } catch (err) {
        console.error("Failed to load clients", err);
        toast.error("Failed to load client database");
      } finally {
        setLoadingClients(false);
      }
    };
    fetchClients();
  }, []);

  /* ================= FETCH CLIENT ORDERS ================= */
  useEffect(() => {
    if (!selectedClientId) {
      setClientOrders([]);
      setSelectedClient(null);
      return;
    }

    const clientObj = clients.find(c => c._id === selectedClientId);
    setSelectedClient(clientObj || null);

    const fetchOrders = async () => {
      try {
        setLoadingOrders(true);
        // Fetch both completed and delivered orders in parallel
        // (completed = closed via web app, delivered = closed via partner app)
        const [completedRes, deliveredRes] = await Promise.all([
          apiClient.get(`/orders?limit=200&status=completed`),
          apiClient.get(`/orders?limit=200&status=delivered`),
        ]);
        const completedOrders = completedRes.data?.data || [];
        const deliveredOrders = deliveredRes.data?.data || [];

        // Merge and deduplicate by _id
        const seen = new Set();
        const allOrders = [...completedOrders, ...deliveredOrders].filter(o => {
          if (seen.has(o._id)) return false;
          seen.add(o._id);
          return true;
        });

        const filtered = allOrders.filter(
          (o) => o.clientId?._id === selectedClientId
        );
        setClientOrders(filtered);
        
        // Reset selections
        setSelectedOrderId("");
        setSelectedOrder(null);
        setReturnQuantities({});
        setDispositions({});
        setNotes("");
      } catch (err) {
        console.error("Failed to load orders", err);
        toast.error("Failed to load client's purchase history");
      } finally {
        setLoadingOrders(false);
      }
    };
    fetchOrders();
  }, [selectedClientId, clients]);

  /* ================= SELECT ORDER ================= */
  const handleSelectOrder = (orderId) => {
    const orderObj = clientOrders.find(o => o._id === orderId);
    setSelectedOrderId(orderId);
    setSelectedOrder(orderObj || null);
    
    // Initialise return inputs
    const initialQtys = {};
    const initialDispositions = {};
    if (orderObj && orderObj.items) {
      orderObj.items.forEach(item => {
        initialQtys[item._id] = 0;
        initialDispositions[item._id] = "restock"; // default disposition
      });
    }
    setReturnQuantities(initialQtys);
    setDispositions(initialDispositions);
  };

  /* ================= QUANTITY CHANGE ================= */
  const handleQtyChange = (itemId, change, maxQty) => {
    const current = returnQuantities[itemId] || 0;
    const next = Math.max(0, Math.min(maxQty, current + change));
    setReturnQuantities(prev => ({
      ...prev,
      [itemId]: next
    }));
  };

  const handleDispositionChange = (itemId, val) => {
    setDispositions(prev => ({
      ...prev,
      [itemId]: val
    }));
  };

  /* ================= CALCULATION ================= */
  const returnItemsList = selectedOrder 
    ? (selectedOrder.items || []).filter(item => (returnQuantities[item._id] || 0) > 0)
    : [];

  const returnSubtotal = returnItemsList.reduce((sum, item) => {
    const qty = returnQuantities[item._id] || 0;
    return sum + (qty * (item.price || 0));
  }, 0);

  /* ================= SUBMIT PROCESS ================= */
  const handleProcessReturn = async () => {
    if (returnItemsList.length === 0) {
      toast.warning("Please specify at least 1 item to return.");
      return;
    }

    try {
      setIsProcessing(true);
      
      const payload = {
        clientId: selectedClientId,
        originalOrderId: selectedOrderId,
        notes,
        items: returnItemsList.map(item => ({
          productId: item.productId?._id || item.productId,
          productName: item.productName,
          quantityValue: item.quantityValue,
          quantityUnit: item.quantityUnit,
          quantity: returnQuantities[item._id],
          warehouseId: item.warehouseId?._id || item.warehouseId,
          expiryDate: item.expiryDate || null,
          batchId: item.batchId || "",
          disposition: dispositions[item._id] || "restock"
        }))
      };

      const res = await apiClient.post("/orders/return", payload);
      const data = res.data?.data;

      const currentBalance = selectedClient?.balance || 0;
      const creditApplied = returnSubtotal;
      const remainingBalance = currentBalance - creditApplied;

      setGeneratedReceipt({
        receiptId: data?.returnId || `RET-${Date.now().toString().slice(-6)}`,
        date: data?.createdAt ? new Date(data.createdAt) : new Date(),
        client: selectedClient,
        originalOrder: selectedOrder,
        returnedItems: returnItemsList.map(item => ({
          name: item.productName,
          quantityValue: item.quantityValue,
          quantityUnit: item.quantityUnit,
          returnedQty: returnQuantities[item._id],
          price: item.price,
          totalPrice: returnQuantities[item._id] * item.price,
          warehouseName: item.warehouseName || "N/A",
          batchId: item.batchId || "N/A",
          expiryDate: item.expiryDate,
          disposition: dispositions[item._id]
        })),
        notes,
        financials: {
          previousBalance,
          creditApplied,
          remainingBalance
        }
      });

      // Update client balance on frontend state
      setSelectedClient(prev => prev ? { ...prev, balance: remainingBalance } : null);
      setClients(prev => prev.map(c => c._id === selectedClientId ? { ...c, balance: remainingBalance } : c));

      setShowReceipt(true);
      toast.success("Return processed successfully!");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to process return order");
    } finally {
      setIsProcessing(false);
    }
  };

  /* ================= RESET FORM ================= */
  const handleReset = () => {
    setSelectedClientId("");
    setSelectedClient(null);
    setClientOrders([]);
    setSelectedOrderId("");
    setSelectedOrder(null);
    setReturnQuantities({});
    setDispositions({});
    setNotes("");
    setShowReceipt(false);
    setGeneratedReceipt(null);
  };

  /* ================= FILTER CLIENTS ================= */
  const filteredClients = clients.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.phone?.includes(searchTerm)
  );

  const previousBalance = selectedClient?.balance || 0;

  return (
    <div className="bg-slate-50 min-h-screen pb-12">
      {/* ================= HEADER ================= */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Return Order</h1>
          <p className="text-sm text-slate-500 mt-1">
            Configure product returns, manage stock write-backs, and preview ledger adjustments.
          </p>
        </div>
      </div>

      {/* ================= SEARCH BAR ROW ================= */}
      <div className="bg-white border-b border-slate-200/80 p-3">
        <div className="max-w-[1400px] mx-auto">
          <SearchableClientSelect
            options={clients}
            value={selectedClientId}
            onChange={(id) => setSelectedClientId(id)}
            placeholder="Search client by name or phone..."
          />
        </div>
      </div>

      {/* ================= SELECTED CLIENT PROFILE ROW ================= */}
      {selectedClient && (
        <div className="bg-white border-b border-slate-200 text-xs p-3">
          <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-slate-600">
              <div>
                <span className="text-slate-400 font-semibold mr-1.5">Client:</span>
                <span className="font-bold text-slate-800">{selectedClient.name}</span>
              </div>
              {selectedClient.phone && (
                <div>
                  <span className="text-slate-400 font-semibold mr-1.5">Phone:</span>
                  <span className="font-semibold text-slate-700">{selectedClient.phone}</span>
                </div>
              )}
              {selectedClient.address && (
                <div>
                  <span className="text-slate-400 font-semibold mr-1.5">Address:</span>
                  <span className="font-semibold text-slate-700" title={selectedClient.address}>
                    {selectedClient.address}
                  </span>
                </div>
              )}
            </div>
            <div className="pt-2 sm:pt-0 sm:border-l border-slate-200 sm:pl-6 shrink-0 flex items-center gap-2">
              <span className="text-slate-400 font-semibold">Ledger Balance:</span>
              <span className={`font-black text-sm ${previousBalance > 0 ? "text-red-600" : previousBalance < 0 ? "text-green-600" : "text-slate-600"}`}>
                {previousBalance > 0 ? `₹${previousBalance.toLocaleString()} (Debit)` : previousBalance < 0 ? `₹${Math.abs(previousBalance).toLocaleString()} (Credit)` : "₹0.00"}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="w-full bg-white grid grid-cols-1 lg:grid-cols-12 border-b border-slate-200 min-h-[calc(100vh-220px)] items-stretch">
        
        {/* ================= LEFT SIDEBAR (SELECTION) ================= */}
        <div className="lg:col-span-4 border-r border-slate-200 p-5 flex flex-col">
          <div className="bg-[#172554] px-5 py-3 -mx-5 -mt-5 mb-0 text-white font-bold uppercase tracking-wider text-[10px]">
            2. Select Completed Order
          </div>
          
          {!selectedClientId ? (
            <div className="py-8 text-center text-slate-400 text-xs italic flex-1 flex items-center justify-center">
              Select a client first to view purchase history.
            </div>
          ) : loadingOrders ? (
            <div className="py-8 flex-1 flex justify-center items-center">
              <RefreshCw className="w-5 h-5 text-[#172554] animate-spin" />
            </div>
          ) : clientOrders.length > 0 ? (
            <div className="flex flex-col overflow-y-auto flex-1 max-h-[550px] -mx-5 -mb-5 border-t border-slate-100">
              {clientOrders.map(order => {
                const isSelected = order._id === selectedOrderId;
                return (
                  <button
                    key={order._id}
                    onClick={() => handleSelectOrder(order._id)}
                    className={`w-full text-left px-5 py-3 transition-all text-xs flex justify-between items-center border-b border-slate-100 last:border-b-0 ${
                      isSelected 
                        ? "bg-blue-50/70 text-[#172554] font-semibold border-l-4 border-l-[#172554] pl-[16px]"
                        : "bg-white hover:bg-slate-50 text-slate-600"
                    }`}
                  >
                    <div className="flex flex-col gap-1">
                      <span className="font-bold">{order.orderId}</span>
                      <span className="text-[10px] text-slate-400 flex items-center gap-1">
                        <Calendar size={11} /> {formatAnyDateToDDMMYYYY(order.createdAt)}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="font-black text-slate-900">
                        ₹{order.paymentDetails?.totalAmount?.toLocaleString()}
                      </span>
                      <span className="block text-[9px] uppercase font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded-none border border-green-100 mt-1 w-fit ml-auto">
                        {order.paymentDetails?.paymentStatus || "completed"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center text-slate-400 text-xs italic flex-1 flex items-center justify-center">
              No completed sales orders found.
            </div>
          )}
        </div>

        {/* ================= RIGHT WORKSPACE (RETURN ITEMS) ================= */}
        <div className="lg:col-span-8 p-5 flex flex-col bg-slate-50/20">
          {!selectedOrderId ? (
            <div className="p-12 text-center flex flex-col justify-center items-center flex-1">
              <div className="p-4 bg-blue-50 text-[#172554] rounded-full mb-3">
                <ShoppingBag size={32} />
              </div>
              <h3 className="text-sm font-bold text-[#172554] uppercase tracking-wider">
                Returns Workspace
              </h3>
              <p className="text-xs text-slate-500 mt-2 max-w-[320px] mx-auto leading-relaxed">
                Select a client and their completed purchase order on the left column to begin configuring the items list.
              </p>
            </div>
          ) : (
            <div className="flex flex-col flex-1">
              <div className="bg-blue-50 border-b border-slate-200 px-5 py-3 -mx-5 -mt-5 mb-0">
                <h3 className="text-[11px] font-bold text-slate-800 uppercase tracking-wider">
                  3. Configure Return Items (Order: {selectedOrder?.orderId})
                </h3>
                <p className="text-[10px] text-slate-500 mt-1 font-medium leading-normal">
                  Adjust quantity to return and select whether the items should be restocked to inventory or written-off as waste.
                </p>
              </div>

              {/* ITEMS GRID */}
              <div className="border-t border-b border-slate-200 overflow-hidden bg-white -mx-5">
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr className="bg-[#172554] text-white font-bold uppercase tracking-wider text-[10px]">
                      <th className="py-3 px-4 text-left">Product Details</th>
                      <th className="py-3 px-2 text-center w-[120px]">Purchase Qty</th>
                      <th className="py-3 px-2 text-center w-[160px]">Return Qty</th>
                      <th className="py-3 px-2 text-center w-[160px]">Stock Action</th>
                      <th className="py-3 px-4 text-right w-[120px]">Return Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedOrder?.items || []).map((item) => {
                      const qtyToReturn = returnQuantities[item._id] || 0;
                      const action = dispositions[item._id] || "restock";
                      const itemCredit = qtyToReturn * (item.price || 0);

                      return (
                        <tr key={item._id} className="border-b border-slate-100 hover:bg-slate-50/50 transition last:border-b-0">
                          <td className="py-3 px-4">
                            <div className="flex flex-col">
                              <span className="font-bold text-slate-800 leading-tight">
                                {item.productName} ({item.quantityValue}{item.quantityUnit})
                              </span>
                              <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[9.5px] text-slate-400">
                                {item.warehouseName && (
                                  <span>Loc: <span className="font-semibold text-slate-600">{item.warehouseName}</span></span>
                                )}
                                {item.batchId && item.batchId !== "N/A" && (
                                  <span>Batch: <span className="font-mono text-slate-600">{item.batchId}</span></span>
                                )}
                                {item.expiryDate && (
                                  <span>Exp: <span className="font-semibold text-slate-600">{formatAnyDateToDDMMYYYY(item.expiryDate)}</span></span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-center font-bold text-slate-700">
                            {item.quantity} units
                          </td>
                          <td className="py-3 px-2 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => handleQtyChange(item._id, -1, item.quantity)}
                                disabled={qtyToReturn === 0}
                                className="w-6 h-6 rounded-none border border-slate-200 flex items-center justify-center text-xs font-black text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent"
                              >
                                -
                              </button>
                              <span className="w-8 font-black text-slate-900 text-sm text-center">
                                {qtyToReturn}
                              </span>
                              <button
                                onClick={() => handleQtyChange(item._id, 1, item.quantity)}
                                disabled={qtyToReturn === item.quantity}
                                className="w-6 h-6 rounded-none border border-slate-200 flex items-center justify-center text-xs font-black text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-transparent"
                              >
                                +
                              </button>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <select
                              value={action}
                              onChange={(e) => handleDispositionChange(item._id, e.target.value)}
                              disabled={qtyToReturn === 0}
                              className={`text-[10px] p-1 border rounded-none focus:outline-none ${
                                qtyToReturn === 0 
                                  ? "bg-slate-50 border-slate-100 text-slate-300"
                                  : action === "restock"
                                    ? "border-green-200 text-green-700 bg-green-50/50 font-semibold"
                                    : "border-orange-200 text-orange-700 bg-orange-50/50 font-semibold"
                              }`}
                            >
                              <option value="restock">Restock (Sellable)</option>
                              <option value="dispose">Discard (Damaged)</option>
                            </select>
                          </td>
                          <td className="py-3 px-4 text-right font-bold text-slate-800 text-sm">
                            ₹{itemCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* BOTTOM SUMMARY & PROCESS BUTTON (EDGE-TO-EDGE) */}
              <div className="mt-auto -mx-5 -mb-5 bg-white border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-0 divide-y md:divide-y-0 md:divide-x divide-slate-200 items-stretch">
                {/* Left Side: Return Notes */}
                <div className="flex flex-col">
                  <div className="bg-slate-100/70 text-slate-600 uppercase text-[10px] font-bold tracking-wider px-4 py-2 border-b border-slate-200/60">
                    Return Notes / Reason for Return
                  </div>
                  <textarea
                    rows={2}
                    placeholder="Write notes regarding items condition, return reasons, etc..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full p-4 text-xs bg-white focus:outline-none resize-none flex-1 min-h-[80px] border-0"
                  ></textarea>
                </div>

                {/* Right Side: Totals & Process button */}
                <div className="flex flex-col bg-white">
                  <div className="bg-slate-100/70 text-slate-600 uppercase text-[10px] font-bold tracking-wider px-4 py-2 border-b border-slate-200/60">
                    Refund Summary
                  </div>
                  <div className="p-4 flex flex-col gap-3 justify-between flex-1">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-slate-500 font-semibold">Total Credit Refund:</span>
                      <span className="text-lg font-black text-slate-900 font-mono">
                        ₹{returnSubtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={handleReset}
                        className="flex-1 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-md border border-slate-200 transition text-xs"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleProcessReturn}
                        disabled={isProcessing || returnSubtotal === 0}
                        className="flex-[2] py-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-md transition disabled:opacity-50 text-xs flex justify-center items-center gap-1.5 shadow-sm"
                      >
                        {isProcessing ? (
                          <>
                            <RefreshCw size={13} className="animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 size={13} />
                            Process Return
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ================= CREDIT NOTE RECEIPT MODAL (PROTOTYPE) ================= */}
      {showReceipt && generatedReceipt && createPortal(
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-2">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-[960px] max-h-[90vh] flex flex-col overflow-hidden">
            
            {/* Modal Header */}
            <div className="px-6 py-4 bg-[#172554] border-b flex justify-between items-center text-white">
              <h2 className="text-lg font-semibold">
                Credit Note & Returns Receipt
              </h2>
              <button type="button" onClick={handleReset} className="text-xl text-white">
                ✕
              </button>
            </div>

            {/* Receipt Content Wrapper */}
            <div className="overflow-y-auto text-xs text-slate-600 flex flex-col flex-1 max-h-[550px]">
              
              {/* Receipt Header Banner */}
              <div className="text-center py-5 px-6 border-b border-slate-200 bg-slate-50/20 flex flex-col gap-1">
                <h2 className="text-lg font-black text-slate-800">STAR NUTRITION</h2>
                <p className="text-[10px] text-slate-400">RETURNS & CREDIT ADJUSTMENTS</p>
                <div className="mt-2 text-[10px] text-slate-500 font-mono flex justify-center gap-4">
                  <span>Credit Note ID: <b>{generatedReceipt.receiptId}</b></span>
                  <span>Date: <b>{generatedReceipt.date.toLocaleString()}</b></span>
                </div>
              </div>

              {/* Two Column Layout Grid */}
              <div className="grid grid-cols-1 md:grid-cols-12 flex-1 items-stretch">
                
                {/* LEFT COLUMN: RETURNED ITEMS TABLE (Col Span 7) */}
                <div className="md:col-span-7 flex flex-col border-r border-slate-200 bg-white">
                  <div className="bg-slate-50 border-b border-slate-200 px-5 py-3 text-slate-700 font-bold uppercase tracking-wider text-[10px]">
                    Returned Items List
                  </div>
                  <div className="flex-1 bg-white">
                    <table className="w-full border-collapse text-[11px] leading-tight">
                      <thead>
                        <tr className="bg-slate-100/30 border-b border-slate-200 font-bold text-slate-500 text-[10px] uppercase">
                          <th className="py-2.5 px-5 text-left">Item Details</th>
                          <th className="py-2.5 px-3 text-center w-[120px]">Return Status</th>
                          <th className="py-2.5 px-5 text-right w-[100px]">Total Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {generatedReceipt.returnedItems.map((item, idx) => (
                          <tr key={idx} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/40">
                            <td className="py-3 px-5">
                              <span className="font-bold text-slate-800">{item.name}</span>
                              <div className="text-[9px] text-slate-400 flex flex-wrap gap-x-1.5 mt-0.5">
                                <span>Qty: {item.returnedQty}</span> | 
                                <span>Loc: {item.warehouseName}</span> | 
                                {item.batchId !== "N/A" && <span>Batch: {item.batchId}</span>}
                              </div>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                                item.disposition === "restock"
                                  ? "bg-green-50 text-green-700 border border-green-200"
                                  : "bg-orange-50 text-orange-700 border border-orange-200"
                              }`}>
                                {item.disposition === "restock" ? "Restocked" : "Discarded"}
                              </span>
                            </td>
                            <td className="py-3 px-5 text-right font-semibold text-slate-800">
                              ₹{item.totalPrice?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* RIGHT COLUMN: CLIENT, TRANSACTION & FINANCE DETAILS (Col Span 5) */}
                <div className="md:col-span-5 flex flex-col bg-slate-50/30">
                  
                  {/* Client Details Section */}
                  <div className="p-4 border-b border-slate-200 bg-white">
                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                      Client Details
                    </h4>
                    <p className="font-bold text-slate-800">{generatedReceipt.client?.name}</p>
                    <p className="text-slate-500 text-[10px] mt-0.5">{generatedReceipt.client?.phone || "No Phone"}</p>
                    <p className="text-slate-500 text-[10px] truncate mt-0.5" title={generatedReceipt.client?.address}>
                      {generatedReceipt.client?.address || "No Address"}
                    </p>
                  </div>

                  {/* Original Transaction Section */}
                  <div className="p-4 border-b border-slate-200 bg-white">
                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                      Original Transaction
                    </h4>
                    <p className="font-semibold text-slate-700 text-[10px]">Order ID: <b>{generatedReceipt.originalOrder?.orderId}</b></p>
                    <p className="text-slate-500 text-[10px] mt-0.5">
                      Order Date: {formatAnyDateToDDMMYYYY(generatedReceipt.originalOrder?.createdAt)}
                    </p>
                    <p className="text-slate-500 text-[10px] mt-0.5">
                      Original Price: ₹{generatedReceipt.originalOrder?.paymentDetails?.totalAmount?.toLocaleString()}
                    </p>
                  </div>

                  {/* Refund Credit Summary Section */}
                  <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center text-xs font-bold text-slate-800">
                    <span>Refund / Credit Amount:</span>
                    <span className="text-sm font-black text-[#172554]">
                      ₹{generatedReceipt.financials.creditApplied?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>

                  {/* Client Ledger Preview Section */}
                  <div className="p-4 border-b border-slate-200 bg-blue-50/20 flex flex-col gap-1.5 text-[11px]">
                    <h4 className="text-[10px] font-bold text-[#172554] uppercase tracking-wider mb-1">
                      Client Ledger Adjustment Preview
                    </h4>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Previous outstanding debt:</span>
                      <span className="font-semibold text-slate-700">₹{generatedReceipt.financials.previousBalance?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Credit Note credit note:</span>
                      <span className="font-semibold text-green-600">-₹{generatedReceipt.financials.creditApplied?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between pt-1 border-t border-slate-200/50 font-bold">
                      <span className="text-[#172554]">New outstanding debt:</span>
                      <span className={`${generatedReceipt.financials.remainingBalance > 0 ? "text-red-600" : generatedReceipt.financials.remainingBalance < 0 ? "text-green-600" : "text-slate-700"}`}>
                        {generatedReceipt.financials.remainingBalance > 0 
                          ? `₹${generatedReceipt.financials.remainingBalance.toLocaleString()} (Debit)`
                          : generatedReceipt.financials.remainingBalance < 0
                            ? `₹${Math.abs(generatedReceipt.financials.remainingBalance).toLocaleString()} (Credit Advance)`
                            : "₹0.00"
                        }
                      </span>
                    </div>
                  </div>

                  {/* Notes Section */}
                  {generatedReceipt.notes && (
                    <div className="p-4 bg-slate-50/50 text-[10px] text-slate-500 italic">
                      <b>Notes:</b> {generatedReceipt.notes}
                    </div>
                  )}

                </div>

              </div>

            </div>

            {/* Modal Footer Controls */}
            <div className="px-6 py-4 border-t bg-white flex justify-end gap-3">
              <button
                type="button"
                onClick={handleReset}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold px-6 py-2 rounded-md transition-colors text-sm border border-slate-200"
              >
                Close & Clear Workspace
              </button>
            </div>

          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default ReturnOrder;
